import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Chip, Grid, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import PaymentIcon from '@mui/icons-material/Payment';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../session/InternalAuthProvider';
import { AccountData, PaymentLinkPaymentStatus, PaymentLinkService } from '../../api';
import { getDateFormat } from '../../common/getDateAndTimeFormat';

export const PendingPaymentLinksIndicator: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { shortDateFormat } = getDateFormat(account);

  const { data: pendingPaymentLinks, isLoading } = useSWR(
    'pendingPaymentLinks',
    () =>
      PaymentLinkService.getPaymentLinks(1, 10, undefined, [PaymentLinkPaymentStatus.PENDING_PAYMENT], false, false),
    { refreshInterval: 300000 },
  );

  if (isLoading || !pendingPaymentLinks?.length) {
    return null;
  }

  const count = pendingPaymentLinks.length;
  const hasMoreThanOnePage = count >= 10;
  const displayCount = hasMoreThanOnePage ? '10+' : count.toString();

  // Find the next upcoming payment link by order date
  const sortedLinks = pendingPaymentLinks
    .filter((link) => link.order?.date)
    .sort((a, b) => moment(a.order.date).valueOf() - moment(b.order.date).valueOf());

  const nextUpcomingDate = sortedLinks.length > 0 ? moment(sortedLinks[0].order.date).format(shortDateFormat) : null;

  const handleClick = () => {
    navigate('/dashboard/payment-links?status=pending_payment');
  };

  return (
    <Tooltip title={t('paymentlink.unpaidLinksTooltip')} arrow>
      <Grid onClick={handleClick} sx={{ cursor: 'pointer' }}>
        <Chip
          icon={<PaymentIcon />}
          label={
            <Grid sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                {displayCount} {t('paymentlink.pendingLabel')}
              </Typography>
              {nextUpcomingDate && (
                <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.8, lineHeight: 1.1 }}>
                  {t('paymentlink.nextLabel')}: {nextUpcomingDate}
                </Typography>
              )}
            </Grid>
          }
          color="warning"
          variant="outlined"
          size="small"
          sx={{
            height: 'auto',
            minHeight: nextUpcomingDate ? 48 : 32,
            '& .MuiChip-label': {
              padding: '6px 8px',
            },
            '&:hover': {
              backgroundColor: 'warning.light',
              color: 'warning.contrastText',
            },
          }}
        />
      </Grid>
    </Tooltip>
  );
};
